import type { KeyboardEvent, MouseEvent } from 'react'

/**
 * One mechanism for every surface where the SURFACE IS THE CONTROL — a card in
 * a card list, a row in a table — so a collection opens the same way whichever
 * view it is in, and by keyboard as well as by pointer.
 *
 * It is a plain function returning props — not a component, because the element
 * differs while the behavior does not, and not a hook, because it holds no
 * state and a row list calls it once per row: a `<tr>` cannot be swapped for an `<article>`, and the
 * way a UI ends up with rows that behave three ways is the third table writing
 * its own `onClick` because copying twenty lines was easier than sharing them.
 * Same reasoning as `usePopover` owning dismissal for every floating thing.
 *
 * **Keyboard operability is not optional here.** `40-design-system.md`: a
 * control that cannot be operated from the keyboard is unfinished, not pending
 * a11y — and the no-component-library decision means nobody else supplies it.
 *
 * ## Why a handler rather than an overlay button
 *
 * The usual trick is a real `<button>` whose `::after` covers the surface. It
 * is tidier and it silently breaks text selection: the overlay captures the
 * drag, so a gabbai cannot copy a rationale or an address out of a record. So
 * the surface takes the handler and announces itself as a button, and a drag
 * that selected text is ignored.
 */
export function opensRecord({
  onOpen,
  label,
}: {
  onOpen: () => void
  /** What opening this does, naming the record — "Open the Katz household".
   *  A list of identical "Open" labels is the failure this exists to avoid. */
  label: string
}) {
  /*
   * A click that ended a text selection is not a click on the record. Without
   * this, selecting a line opens the editor on mouse-up — the exact moment the
   * person was reading rather than navigating.
   */
  const selectedText = () => !(window.getSelection()?.isCollapsed ?? true)

  const open = (e: MouseEvent | KeyboardEvent) => {
    // Anything that answers for itself — a toggle, a menu, a link — says so
    // with `data-no-open`, which is one attribute rather than a pair of
    // hand-written stopPropagation handlers per call site.
    if ((e.target as HTMLElement).closest('[data-no-open]')) return
    if (selectedText()) return
    onOpen()
  }

  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
    onClick: open,
    onKeyDown: (e: KeyboardEvent) => {
      // Enter and Space are what a button answers to; nothing else should open
      // a record while somebody is tabbing past it.
      if (e.key !== 'Enter' && e.key !== ' ') return
      /*
       * A control inside keeps its OWN default, which is why this returns
       * before preventing anything: the key event bubbles, so swallowing it
       * here would stop Space from flipping the toggle it was actually pressed
       * on. Checking the target first is the difference between a row that
       * opens and a row that eats its own switches.
       */
      if ((e.target as HTMLElement).closest('[data-no-open]')) return
      // Space would scroll the page, and Enter inside a form would submit it.
      e.preventDefault()
      open(e)
    },
  }
}
